import { UploadOutlined } from '@ant-design/icons';
import { Space, message, Upload, type UploadProps } from 'antd';
import type { RcFile, UploadFile } from 'antd/lib/upload';
import { useField } from 'formik';
import { isObject, isNil, isEmpty, compact } from 'lodash/fp';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type UploadFileWithPreviewFormDataFragment } from '../../api/fragments/UploadFileWithPreviewFormData';
import { validateExtensions } from '../../utilities/extensions';
import FormItem, { type FormItemProps } from './FormItem';

export type MultiPictureWallUploadFieldProps = Omit<UploadProps, 'listType' | 'maxCount' | 'accept' | 'fileList'> & {
    name: string;
    extensions: string[];
    label?: string;
    required?: boolean;
    sizeLimitInMiB?: number;
    tooltip?: FormItemProps['tooltip'];
    style?: FormItemProps['style'];
    max?: number;
};

type MultiPictureType = UploadFile<UploadFileWithPreviewFormDataFragment> & { id: string };

const MultiPictureWallUploadField = ({
    name,
    label,
    extensions,
    required = false,
    sizeLimitInMiB = 2,
    tooltip,
    style,
    max,
    disabled = false,
}: MultiPictureWallUploadFieldProps) => {
    const { t } = useTranslation(['common']);

    const accept = useMemo(() => extensions.join(','), [extensions]);

    const [field, meta, helpers] = useField<MultiPictureType[]>(name);

    const defaultFileList = useMemo(() => {
        // Using meta.value for handling unmounted effect
        if (isNil(field.value) || isEmpty(field.value)) {
            return [];
        }

        return field.value.map(file => {
            if (!(file instanceof File) && isObject(file)) {
                // Cast as api fragment
                const value = file as unknown as UploadFileWithPreviewFormDataFragment;

                return {
                    ...file,
                    uid: value.id,
                    name: value.filename,
                    thumbUrl: value.preview?.url || value.url,
                };
            }

            return {
                ...file,
                id: file.uid,
                thumbUrl: URL.createObjectURL(file),
            };
        });
    }, [field.value]);

    const onRemove = useCallback<NonNullable<UploadProps['onRemove']>>(
        file => {
            // manually touch the file
            helpers.setTouched(true);

            // assign file to our known value first
            const castedFile = file as RcFile & { id: string };
            const currentValue = field.value ?? [];
            // delete the file
            helpers.setValue(currentValue.filter(item => item.uid !== castedFile.uid && item.id !== castedFile.id));
        },
        [field.value, helpers]
    );

    const beforeUpload = useCallback<NonNullable<UploadProps['beforeUpload']>>(
        async (file, fileList) => {
            if (file.size > sizeLimitInMiB * 1024 * 1024) {
                message.error(t('common:upload.messages.sizeExceedsLimit', { limit: sizeLimitInMiB }));

                return Upload.LIST_IGNORE;
            }

            if (fileList?.length > 0 && file.uid === fileList[fileList.length - 1].uid) {
                const validatingFilesResult = await Promise.all(
                    fileList.map(async (f: RcFile) => {
                        const reader = new FileReader();
                        const promise = new Promise<RcFile>((resolve, reject) => {
                            reader.onload = async event => {
                                const content = event.target.result as string;
                                const validationResult = await validateExtensions(content, f.name);
                                if (validationResult.valid) {
                                    return resolve(f);
                                }

                                return resolve(null);
                            };

                            reader.onerror = event => resolve(null);
                        });
                        reader.readAsBinaryString(f);

                        return Promise.resolve(promise);
                    })
                );
                const validatedFiles = compact(validatingFilesResult);
                if (!validatedFiles.length) {
                    message.error(t('common:upload.messages.fileError'));
                }
                // TODO: Find a way to assign the type without casting
                const list = validatedFiles as unknown as MultiPictureType[];
                helpers.setValue([...(field.value ?? []), ...list]);
            }

            return false;
        },
        [field.value, helpers, sizeLimitInMiB, t]
    );

    return (
        <FormItem label={label} meta={meta} name={name} required={required} style={style} tooltip={tooltip}>
            <Upload
                accept={accept}
                beforeUpload={beforeUpload}
                disabled={disabled}
                // we need to use filelist because defaultFileList cannot reflect the formik value changes.
                fileList={defaultFileList}
                listType="picture-card"
                maxCount={max}
                onRemove={onRemove}
                showUploadList={{
                    showPreviewIcon: false,
                }}
                multiple
            >
                <Space>
                    <UploadOutlined />
                    {t('common:upload.button')}
                </Space>
            </Upload>
        </FormItem>
    );
};

export default MultiPictureWallUploadField;
